import { AccommodationCard } from "@@/products/accommodations/accommodation-card";
import {
    AccommodationDatePill,
    AccommodationQuantityPill,
} from "@@/products/accommodations/accommodation-pill/accommodation-pill";
import { useBookingAccommodationContext } from "@@/products/accommodations/booking-accommodation-context";
import { getAccommodationSKU } from "@@/products/accommodations/booking-accommodation-modal";

import { useCalculatedInventory } from "@@/products/accommodations/hooks/use-calculated-accommodation";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { ForEach } from "@@/shared/for-each";
import { LayoutCell } from "@@/shared/layout-cell";
import { LayoutGrid } from "@@/shared/layout-grid";
import { TextBox } from "@@/shared/text";
import { useTheme } from "@emotion/react";
import type { Accommodation, Product } from "@towni/common";
import { isTimeRange, sortBy } from "@towni/common";
import React, { useMemo } from "react";

type Props = {
    product: Product;
    accommodation: Accommodation;
};

const _AccommodationPicker = (props: Props) => {
    const theme = useTheme();

    const { time } = useBookingAccommodationContext(state => ({
        quantities: state.quantities,
        time: state.timeRange,
    }));

    const timeRange = isTimeRange(time) ? time : undefined;

    const calculatedInventoryResult = useCalculatedInventory(
        props.product,
        props.accommodation,
    );

    const [hasInventory, hasNoInventory] = useMemo(() => {
        const hasInventory = calculatedInventoryResult.filter(
            i =>
                i.inventoryDetails.length > 0 &&
                i.inventoryDetails.some(r => r.isAvailable),
        );
        const hasNoInventoryButHasResources = calculatedInventoryResult.filter(
            i =>
                i.inventoryDetails.length === 0 ||
                !i.inventoryDetails.some(r => r.isAvailable),
        );
        return [hasInventory, hasNoInventoryButHasResources] as const;
    }, [calculatedInventoryResult]);

    return (
        <>
            <LayoutGrid
                padding={{ top: 20 }}
                css={{
                    width: "100%",
                    height: "100%",
                    gridTemplateColumns: `1fr`,
                    overflowY: "scroll",
                }}>
                <LayoutCell>
                    <FlexColumn padding={{ leftRight: 20 }}>
                        <ForEach
                            itemOf={hasInventory.sort(
                                sortBy(
                                    i =>
                                        i.inventoryDetails.filter(
                                            r => r.isAvailable,
                                        ).length,
                                    "DESCENDING",
                                ),
                            )}
                            divider={<VerticalDivider L />}>
                            {i => {
                                return (
                                    <React.Fragment key={i.inventory._id}>
                                        <Conditional when={!!i.inventory.title}>
                                            <FlexColumn
                                                padding={{
                                                    top: 10,
                                                    bottom: 20,
                                                }}
                                                crossAxis="center">
                                                <TextBox
                                                    text={i.inventory.title}
                                                    weight="700"
                                                    size={1.125}
                                                />
                                                <VerticalDivider XS />
                                                <TextBox
                                                    text={
                                                        i.inventory.description
                                                    }
                                                    align="center"
                                                    weight="400"
                                                    size={0.875}
                                                    color={
                                                        theme.colors.default
                                                            .text.light40
                                                    }
                                                />
                                            </FlexColumn>
                                        </Conditional>
                                        <ForEach
                                            itemOf={i.inventoryDetails.sort(
                                                sortBy(s =>
                                                    s.isAvailable ? 0 : 1,
                                                ),
                                            )}
                                            divider={<VerticalDivider M />}
                                            endWith={<VerticalDivider M />}>
                                            {group => {
                                                return (
                                                    <AccommodationCard
                                                        product={props.product}
                                                        resource={
                                                            group.resourceGroup
                                                        }
                                                        availability={
                                                            group.availability
                                                        }
                                                        range={timeRange}
                                                        allResources={
                                                            group.resources
                                                        }
                                                        calculatedRates={
                                                            group.rates
                                                        }
                                                        inventory={
                                                            group.inventory
                                                        }
                                                    />
                                                );
                                            }}
                                        </ForEach>
                                    </React.Fragment>
                                );
                            }}
                        </ForEach>
                    </FlexColumn>

                    <Conditional when={hasNoInventory.length > 0}>
                        <VerticalDivider XL />
                        <FlexColumn
                            crossAxis="center"
                            padding={{ top: 24, bottom: 8 }}
                            className="w-full text-center">
                            <div className="flex w-full items-center justify-center gap-4">
                                <div className="h-px max-w-[200px] flex-grow bg-gray-300" />
                                <TextBox
                                    text={{
                                        sv: "REKOMMENDERAT",
                                        en: "RECOMMENDED",
                                        de: "EMPFOHLEN",
                                    }}
                                    weight="700"
                                    size={1}
                                    className="tracking-wide uppercase"
                                />
                                <div className="h-px max-w-[200px] flex-grow bg-gray-300" />
                            </div>
                            <TextBox
                                text={{
                                    sv: "Här är några alternativ som kan passa just eran bokning. Kan vara så att du behöver justera dina sökkriterier för att boka.",
                                    en: "Here are some options that might suit your booking. You may need to adjust your search criteria to book.",
                                    de: "Hier sind einige Optionen, die zu Ihrer Buchung passen könnten. Möglicherweise müssen Sie Ihre Suchkriterien anpassen, um zu buchen.",
                                }}
                                size={0.9}
                                weight="400"
                                color="#999"
                                className="mt-2 px-3"
                            />
                        </FlexColumn>
                        <FlexColumn padding={{ all: 20 }}>
                            {/* <FlexColumn>
                                <VerticalDivider L />
                                <InfoBox_FlexColumn>
                                    <TextBox
                                        text={{
                                            sv: "Dessa boenden kanske passar dig!",
                                            en: "These accommodations might suit you!",
                                            de: "Diese Unterkünfte könnten Ihnen gefallen!",
                                        }}
                                        weight="700"
                                        size={"ML"}
                                        color={
                                            theme.colors.default.text.light10
                                        }
                                    />
                                    <TextBox
                                        text={{
                                            sv: "Du kan behöva justera dina sökkriterier för att boka. Här är några alternativ som kan passa just eran bokning.",
                                            en: "You may need to adjust your search criteria to book. Here are some options that might suit your booking.",
                                            de: "Möglicherweise müssen Sie Ihre Suchkriterien anpassen, um zu buchen. Hier sind einige Optionen, die zu Ihrer Buchung passen könnten.",
                                        }}
                                        size={0.9}
                                        weight="500"
                                        color={
                                            theme.colors.default.text.light50
                                        }
                                    />
                                </InfoBox_FlexColumn>
                            </FlexColumn> */}
                            <VerticalDivider L />
                            <ForEach
                                itemOf={hasNoInventory.sort(
                                    sortBy(
                                        i =>
                                            i.inventoryDetails.filter(
                                                r => r.isAvailable,
                                            ).length,
                                        "DESCENDING",
                                    ),
                                )}
                                divider={<VerticalDivider L />}>
                                {i => {
                                    return (
                                        <React.Fragment key={i.inventory._id}>
                                            <Conditional
                                                when={!!i.inventory.title}>
                                                <FlexColumn
                                                    padding={{
                                                        top: 10,
                                                        bottom: 20,
                                                    }}
                                                    crossAxis="center">
                                                    <TextBox
                                                        text={i.inventory.title}
                                                        weight="700"
                                                        size={1.125}
                                                    />
                                                    <VerticalDivider XS />
                                                    <TextBox
                                                        text={
                                                            i.inventory
                                                                .description
                                                        }
                                                        align="center"
                                                        weight="400"
                                                        size={0.875}
                                                        color={
                                                            theme.colors.default
                                                                .text.light40
                                                        }
                                                    />
                                                </FlexColumn>
                                            </Conditional>
                                            <ForEach
                                                itemOf={i.inventoryDetails.sort(
                                                    sortBy(s =>
                                                        s.isAvailable ? 0 : 1,
                                                    ),
                                                )}
                                                divider={<VerticalDivider M />}>
                                                {group => {
                                                    return (
                                                        <AccommodationCard
                                                            product={
                                                                props.product
                                                            }
                                                            resource={
                                                                group.resourceGroup
                                                            }
                                                            availability={
                                                                group.availability
                                                            }
                                                            range={timeRange}
                                                            allResources={
                                                                group.resources
                                                            }
                                                            calculatedRates={
                                                                group.rates
                                                            }
                                                            inventory={
                                                                group.inventory
                                                            }
                                                        />
                                                    );
                                                }}
                                            </ForEach>
                                        </React.Fragment>
                                    );
                                }}
                            </ForEach>
                        </FlexColumn>
                    </Conditional>
                </LayoutCell>
            </LayoutGrid>
        </>
    );
};

type PickerProps = {
    product: Product;
};
const AccommodationPicker = (props: PickerProps) => {
    const theme = useTheme();

    const acc = getAccommodationSKU(props.product);

    if (!acc) return <div>No accommodation</div>;
    if (acc._type !== "ACCOMMODATION") return <div>Not an accommodation</div>;

    return (
        <FlexColumn
            fillParent
            css={{
                flexGrow: 1,
                flexShrink: 0,
            }}
            background={{
                color: theme.colors.default.background,
            }}>
            <FlexRow
                fillParentWidth
                mainAxis="center"
                padding={{ topBottom: 14 }}
                css={{
                    boxShadow: `0px 5px 10px 0px ${theme.colors.default.border.asString}`,
                }}>
                <AccommodationDatePill
                    product={props.product}
                    accommodation={acc}
                />
                <HorizontalDivider />
                <AccommodationQuantityPill
                    product={props.product}
                    accommodation={acc}
                />
            </FlexRow>
            <_AccommodationPicker accommodation={acc} product={props.product} />
        </FlexColumn>
    );
};

export { AccommodationPicker };
